import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  TrendingUp, TrendingDown, Trophy, RefreshCcw, 
  Loader2, AlertTriangle, ChevronDown, ChevronUp, BarChart3, Users
} from 'lucide-react';
import { API_URL } from '../utils/apiUrl';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, Area, ComposedChart, Line
} from 'recharts';

// Format currency helper
const formatCurrency = (value) => {
  if (!value && value !== 0) return '₹0';
  return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatCompactCurrency = (value) => {
  if (!value && value !== 0) return '₹0';
  if (value >= 100000) {
    return `₹${(value / 100000).toFixed(1)}L`;
  } else if (value >= 1000) {
    return `₹${(value / 1000).toFixed(1)}K`;
  }
  return `₹${value.toFixed(0)}`;
};

// Custom tooltip component for chart
const ChartTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const gapValue = data.target - data.actual;
    const isShort = gapValue > 0;
    
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-lg">
        <p className="text-white font-medium mb-2">Day {data.day}</p>
        <div className="space-y-1 text-sm">
          <p className="text-blue-400">Target: {formatCurrency(data.target)}</p>
          <p className="text-emerald-400">Actual: {formatCurrency(data.actual)}</p>
          <p className={isShort ? 'text-red-400' : 'text-emerald-400'}>
            {isShort ? 'Shortfall' : 'Surplus'}: {formatCurrency(Math.abs(gapValue))}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const legendFormatter = (value) => <span style={{ color: '#94a3b8' }}>{value}</span>;

const PrincipalBrokerageGapWidget = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [showIndividual, setShowIndividual] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/targets/brokerage-gap/principal`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const result = await res.json();
        setData(result);
      } else {
        setError('Failed to load brokerage gap data');
      }
    } catch (err) {
      console.error('Error fetching principal brokerage gap:', err);
      setError('Failed to load brokerage gap data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Card className="card-dark border-[#C5A059]/30 bg-gradient-to-br from-slate-900 to-slate-800">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="card-dark border-white/10">
        <CardContent className="py-8 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-slate-400">{error || 'No data available'}</p>
          <Button variant="ghost" onClick={fetchData} className="mt-2">
            <RefreshCcw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { aggregate, individual, summary } = data;
  const isSurplus = aggregate.is_surplus;
  const dailyProgress = aggregate.daily_progress || [];

  return (
    <Card className="card-dark border-[#C5A059]/30 bg-gradient-to-br from-slate-900 via-slate-900 to-[#C5A059]/5" data-testid="principal-brokerage-gap-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C5A059]" />
            Firm Brokerage Gap
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-slate-600 text-slate-400">
              {aggregate.month_name}
            </Badge>
            <Button variant="ghost" size="sm" onClick={fetchData} className="h-8 w-8 p-0">
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Aggregate Summary */}
        <div className={`rounded-lg border p-4 ${
          isSurplus 
            ? 'bg-emerald-500/10 border-emerald-500/30' 
            : 'bg-red-500/10 border-red-500/30'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {isSurplus ? (
                <>
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <span className="text-emerald-400 font-medium">Firm Ahead of Target!</span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  <span className="text-red-400 font-medium">Firm Behind Target</span>
                </>
              )}
            </div>
            <Badge className={isSurplus ? 'bg-emerald-600' : 'bg-red-600'}>
              Day {aggregate.day_of_month}
            </Badge>
          </div>

          {/* Summary Line */}
          <div className="bg-slate-800/50 rounded-lg p-3 mb-3">
            <p className="text-slate-400 text-xs mb-1 text-center">Today&apos;s Firm Target</p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span className={`text-sm ${isSurplus ? 'text-emerald-400' : 'text-red-400'}`}>
                {isSurplus ? 'Surplus' : 'Backlog'}: {formatCurrency(isSurplus ? aggregate.surplus : aggregate.backlog)}
              </span>
              <span className="text-slate-500">+</span>
              <span className="text-blue-400 text-sm">Daily: {formatCurrency(aggregate.daily_target)}</span>
              <span className="text-slate-500">=</span>
              <span className="text-white font-bold text-lg">{formatCurrency(aggregate.total_to_generate_today)}</span>
              {isSurplus && <span className="text-yellow-500">🏆</span>}
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-2 text-xs">
            <div className="text-center">
              <p className="text-slate-400">Monthly</p>
              <p className="text-white font-medium">{formatCompactCurrency(aggregate.monthly_target)}</p>
            </div>
            <div className="text-center">
              <p className="text-slate-400">Running</p>
              <p className="text-blue-400 font-medium">{formatCompactCurrency(aggregate.running_target)}</p>
            </div>
            <div className="text-center">
              <p className="text-slate-400">Actual</p>
              <p className="text-emerald-400 font-medium">{formatCompactCurrency(aggregate.actual_mtd)}</p>
            </div>
            <div className="text-center">
              <p className="text-slate-400">Gap</p>
              <p className={`font-medium ${isSurplus ? 'text-emerald-400' : 'text-red-400'}`}>
                {isSurplus ? '+' : '-'}{formatCompactCurrency(isSurplus ? aggregate.surplus : aggregate.backlog)}
              </p>
            </div>
          </div>
        </div>

        {/* Team Summary Badges */}
        <div className="flex items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-slate-400 text-sm">{summary.total_users} Team Members</span>
          </div>
          <Badge className="bg-emerald-600">{summary.users_on_track} On Track</Badge>
          <Badge className="bg-red-600">{summary.users_behind} Behind</Badge>
        </div>

        {/* Chart Toggle */}
        <Button 
          variant="outline" 
          className="w-full border-slate-700 hover:bg-slate-800"
          onClick={() => setShowChart(!showChart)}
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          {showChart ? 'Hide' : 'View'} Aggregate Chart
          {showChart ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
        </Button>

        {/* Aggregate Chart */}
        {showChart && dailyProgress.length > 0 && (
          <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
            <h4 className="text-white font-medium mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#C5A059]" />
              Firm Target vs Actual - {aggregate.month_name}
            </h4>
            
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dailyProgress} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis 
                    dataKey="day" 
                    stroke="#94a3b8" 
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                  />
                  <YAxis 
                    stroke="#94a3b8" 
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    tickFormatter={formatCompactCurrency}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend 
                    wrapperStyle={{ paddingTop: '10px' }}
                    formatter={legendFormatter}
                  />
                  
                  <Line 
                    type="monotone" 
                    dataKey="target" 
                    name="Target (Cumulative)"
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                  />
                  
                  <Area
                    type="monotone"
                    dataKey="actual"
                    name="Actual (Cumulative)"
                    fill={isSurplus ? "url(#colorSurplusPrincipal)" : "url(#colorShortfallPrincipal)"}
                    stroke={isSurplus ? "#10b981" : "#ef4444"}
                    strokeWidth={2}
                  />
                  
                  <defs>
                    <linearGradient id="colorSurplusPrincipal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorShortfallPrincipal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Individual Toggle */}
        <Button 
          variant="outline" 
          className="w-full border-slate-700 hover:bg-slate-800"
          onClick={() => setShowIndividual(!showIndividual)}
        >
          <Users className="w-4 h-4 mr-2" />
          {showIndividual ? 'Hide' : 'View'} Individual Breakdown
          {showIndividual ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
        </Button>

        {/* Individual Breakdown Table */}
        {showIndividual && individual.length > 0 && (
          <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
            <h4 className="text-white font-medium mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#C5A059]" />
              Individual Brokerage Gaps
            </h4>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-800">
                  <tr className="text-slate-400 text-xs">
                    <th className="text-left py-2 px-3">Name</th>
                    <th className="text-center py-2 px-2">Role</th>
                    <th className="text-right py-2 px-2">Monthly</th>
                    <th className="text-right py-2 px-2">Running</th>
                    <th className="text-right py-2 px-2">Actual</th>
                    <th className="text-right py-2 px-2">Gap</th>
                    <th className="text-right py-2 px-2">To Generate</th>
                    <th className="text-center py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {individual.map((user, idx) => (
                    <tr key={user.user_id || idx} className="border-t border-slate-700/50 hover:bg-slate-800/50">
                      <td className="py-2 px-3">
                        <div>
                          <p className="text-white font-medium">{user.user_name}</p>
                          <p className="text-slate-500 text-xs">{user.user_code}</p>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-center">
                        <Badge variant="outline" className="text-xs border-slate-600">
                          {user.role === 'rm' ? 'RM' : 'Dealer'}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 text-right text-white">{formatCompactCurrency(user.monthly_target)}</td>
                      <td className="py-2 px-2 text-right text-blue-400">{formatCompactCurrency(user.running_target)}</td>
                      <td className="py-2 px-2 text-right text-emerald-400">{formatCompactCurrency(user.actual_mtd)}</td>
                      <td className={`py-2 px-2 text-right ${user.is_surplus ? 'text-emerald-400' : 'text-red-400'}`}>
                        {user.is_surplus ? '+' : '-'}{formatCompactCurrency(user.is_surplus ? user.surplus : user.backlog)}
                      </td>
                      <td className="py-2 px-2 text-right text-white font-medium">
                        {formatCompactCurrency(user.total_to_generate_today)}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {user.is_surplus ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-500">✓</span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-500">✗</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PrincipalBrokerageGapWidget;
